import {
  Component,
  Input,
  OnChanges,
  EventEmitter,
  Output,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ProductButton } from '@core/interfaces/interfaces';
import { ProductType } from '@core/models/api/dsm-types';
import { LayoutService } from '@core/services/layout.service';
import { ProductsService } from '@core/services/products.service';
import { ComponentChanges } from '@shared/utils/general.utils';
import { PriceSummaryUtils } from '@shared/utils/hub/price-summary/price-summary.util';
import { Nullable } from '@shared/utils/type.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  PriceSummaryDisplayContent,
  PriceSummaryDisplayProduct,
} from './price-summary-display-content';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductToggleFormComponent } from '@shared/components/forms/product-toggle-form/product-toggle-form.component';
import { HubPageRepresentation } from '@core/constants/pages';
import { Store } from '@ngrx/store';
import { VersionedQuoteActions } from '@core/store/actions';
import { UpdateQuoteResponse } from '@core/models/api/response/update-quote-response.model';
import { VersionedQuoteService } from '@entities/versioned-quote/versioned-quote.service';
import * as VersionedQuoteSelectors from '@core/store/entities/versioned-quote/versioned-quote.selector';
import { PersonaService } from '@core/services/persona.service';
import { PersonaReport } from '@entities/persona/persona.model';

@Component({
    selector: 'nwx-price-summary-display',
    templateUrl: './price-summary-display.component.html',
    styleUrls: ['./price-summary-display.component.scss'],
    standalone: false
})
export class PriceSummaryDisplayComponent implements OnChanges, OnDestroy {
  @Input() content: PriceSummaryDisplayContent = {};
  @Input() protectorContent: PriceSummaryDisplayContent = {};
  @Input() maxSizeMedium: Nullable<boolean>;
  @Input() awaitingProtectorVersionChoice: Nullable<boolean>;

  @Output() addUpsell = new EventEmitter<Nullable<ProductType>>();
  @Output() showPdf = new EventEmitter<void>();

  quoteStatusClassName = 'qs-unknown';
  protectorQuoteResponses: UpdateQuoteResponse[] = [];
  protectorVersionLoaded = false;
  protectorPackageReport: PersonaReport | undefined;

  personaReport$: Observable<PersonaReport>;
  productButtons$: Observable<ProductButton[]>;
  maxSizeSmall$: Observable<boolean>;
  unsubscribe$ = new Subject<void>();

  constructor(
    private productsService: ProductsService,
    private layoutService: LayoutService,
    private modal: NgbModal,
    private store: Store,
    private changeDetector: ChangeDetectorRef,
    private versionedQuoteService: VersionedQuoteService,
    private personaService: PersonaService
  ) {
    this.productButtons$ = this.productsService.getProductButtons(false);
    this.maxSizeSmall$ = this.layoutService.maxSizeSmall();
    this.personaReport$ = this.personaService.getPersonaReport();
    this.versionedQuoteService.getProtectorQuoteResponses()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((protectorQuoteResponses) => {
        this.protectorQuoteResponses = protectorQuoteResponses;
        this.changeDetector.markForCheck();
      });
    
    this.store.select(VersionedQuoteSelectors.isProtectorVersionLoaded)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((protectorVersionLoaded) => {
        this.protectorVersionLoaded = protectorVersionLoaded;
        this.changeDetector.markForCheck();
      });
  }

  ngOnChanges(changes: ComponentChanges<PriceSummaryDisplayComponent>): void {
    if (changes.content) {
      this.quoteStatusClassName = PriceSummaryUtils.selectQuoteStatusClassName(
        this.content.quoteStatusLabel || ''
      );
    }
    this.personaReport$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(personaReport => {
        this.protectorPackageReport = this.personaService.getProtectorPackageReport(personaReport, this.protectorQuoteResponses);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  openProductToggleModal(): void {
    const modal = this.modal.open(ProductToggleFormComponent, {
      centered: true,
      size: 'xl',
    });
    modal.componentInstance.page = HubPageRepresentation;
  }

  trackByProduct(index: number, product: PriceSummaryDisplayProduct): string {
    return index.toString();
  }

  isNumber(str: any): boolean {
    return !isNaN(parseFloat(str)) && str !== '';
  }

  onTabChange(event: any): void {
    const isProtectorTabActive = event.detail?.tab?.id === 'protectorQuoteTab';
    this.store.dispatch(VersionedQuoteActions.updateProtectorVersionTabActive({ 
      protectorVersionTabActive: isProtectorTabActive
    }));
  }
}
